import {useEffect, useMemo, useState} from 'react';

import {appRoute} from '../../../../app-route';
import {CursorPaginator} from '../../../../component/cursor-paginator/cursor-paginator';
import {Meta} from '../../../../component/meta/meta';
import {ActivationPage} from '../../../../layout/activation-page/activation-page';
import {Page} from '../../../../layout/page/page';
import {MainPageContainer} from '../../../../layout/page-card/main-page-card';
import {useDocumentationLinks} from '../../../../provider/help-links/help-links-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useSystem} from '../../../../provider/system/system-hook';
import {ReviewSortingTypeEnum} from '../../../../service/reviews/reviews-type';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {reviewPageSizeOptions} from '../reviews-const';
import {ReviewsHeader} from '../reviews-header/reviews-header';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../reviews-state/reviews-state-type';

import {NeuralNetworkAlert} from './neuralink-alert/neuralink-alert';
import {NewReviewList} from './review-list/new-review-list';
import {ReviewsFilter} from './reviews-filter/reviews-filter';
import {ReviewsFilterDataType} from './reviews-filter/reviews-filter-type';
import * as styles from './review-list-page-content.scss';

export function ReviewListPageContent(): JSX.Element {
    const [isFilterOpened, setIsFilterOpened] = useState(true);
    const {reviews, cursorPagination, isReviewsInProgress} = useReviewStateHook();
    const {getLocalizedString} = useLocale();
    const {licenses} = useLicenses();
    const systemContext = useSystem();
    const {screen} = systemContext;
    const {isMobile} = screen;
    const {featureActivation} = useDocumentationLinks();
    const {replaceQuery: setReviewsStateUrlQuery, queries} = useUrl<
        Omit<ReviewsStateType, 'filterState'> & ReviewsFilterDataType
    >();

    const hasReviews = useMemo(() => Boolean(reviews?.results.length), [reviews]);

    const newFilterComponent = useMemo(() => <ReviewsHeader setIsFilterOpened={setIsFilterOpened} />, []);

    useEffect(() => {
        // eslint-disable-next-line no-undefined
        if (queries.ordering === undefined) {
            setReviewsStateUrlQuery({
                [ReviewsStateUrlQueryNameEnum.sorting]: ReviewSortingTypeEnum.acs,
            });
        }
    }, [queries.ordering]);

    useEffect(() => {
        setIsFilterOpened(!isMobile);
    }, [isMobile]);

    if (!licenses[FeaturesEnum.reviews]?.isActive) {
        return (
            <ActivationPage
                breadCrumbList={[
                    {
                        path: appRoute.reviewsManagement.path,
                        titleLangKey: 'CATEGORY_NAME__REVIEWS',
                    },
                ]}
                interestedMessageLangKey="ACTIVATION_PAGE__INTERESTED_CONTACT_YOUR_MANAGER_TO_ACTIVATE_THIS_SERVICE"
                link={featureActivation[FeaturesEnum.reviews]}
                title={<Locale stringKey="CATEGORY_NAME__REVIEWS" />}
            />
        );
    }

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <Meta title={getLocalizedString('CATEGORY_NAME__REVIEWS')} />
            {newFilterComponent}
            <NeuralNetworkAlert />
            <div className={styles.Reviews_contentWrapper}>
                <div className={styles.Reviews_list}>
                    <NewReviewList />
                    {hasReviews && !isReviewsInProgress && (
                        <CursorPaginator cursorPagination={cursorPagination} pageSizeOptions={reviewPageSizeOptions} />
                    )}
                </div>

                <div className={styles.Reviews_filter} hidden={!isFilterOpened}>
                    <ReviewsFilter />
                </div>
            </div>
        </Page>
    );
}
